import {
  DEFAULT_CANVAS_FONT_FAMILY,
  DEFAULT_CANVAS_FONT_STYLE,
  DEFAULT_CANVAS_FONT_WEIGHT,
} from "./canvasFonts";

const DEFAULT_STROKE = "#1d4ed8";
const DEFAULT_FILL = "#dbeafe";
export const DEFAULT_SHAPE_FILL = DEFAULT_FILL;
export const TRANSPARENT_CANVAS_FILL = "transparent";
const DEFAULT_TEXT = "Text";
const DEFAULT_TEXT_SIZE = 22;
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_TEXT_FILL = "#f8fafc";
const DEFAULT_RECTANGLE_HEIGHT = 96;
const DEFAULT_RECTANGLE_WIDTH = 160;
const MIN_ELEMENT_SIZE = 8;
const DUPLICATE_OFFSET = 24;

export const CANVAS_TOOLS = Object.freeze({
  ARROW: "arrow",
  DIAMOND: "diamond",
  DRAW: "draw",
  ELLIPSE: "ellipse",
  HAND: "hand",
  IMAGE: "image",
  RECTANGLE: "rectangle",
  SELECT: "select",
  TEXT: "text",
});

export const CANVAS_ELEMENT_TYPES = Object.freeze({
  ARROW: "arrow",
  DIAMOND: "diamond",
  ELLIPSE: "ellipse",
  IMAGE: "image",
  PATH: "path",
  RECTANGLE: "rectangle",
  TEXT: "text",
});

export const BOX_ELEMENT_TYPES = Object.freeze([
  CANVAS_ELEMENT_TYPES.DIAMOND,
  CANVAS_ELEMENT_TYPES.ELLIPSE,
  CANVAS_ELEMENT_TYPES.IMAGE,
  CANVAS_ELEMENT_TYPES.RECTANGLE,
]);

export const LABELABLE_ELEMENT_TYPES = Object.freeze([
  CANVAS_ELEMENT_TYPES.DIAMOND,
  CANVAS_ELEMENT_TYPES.ELLIPSE,
  CANVAS_ELEMENT_TYPES.RECTANGLE,
]);

export const isBoxElement = (element) =>
  BOX_ELEMENT_TYPES.includes(element?.type);

export const createCanvasElementId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  return `canvas-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createStyle = (style = {}) => ({
  fill: style.fill ?? DEFAULT_FILL,
  fontFamily: style.fontFamily ?? DEFAULT_CANVAS_FONT_FAMILY,
  fontSize: style.fontSize ?? DEFAULT_TEXT_SIZE,
  fontStyle: style.fontStyle ?? DEFAULT_CANVAS_FONT_STYLE,
  fontWeight: style.fontWeight ?? DEFAULT_CANVAS_FONT_WEIGHT,
  stroke: style.stroke ?? DEFAULT_STROKE,
  strokeWidth: style.strokeWidth ?? DEFAULT_STROKE_WIDTH,
});

export const normalizeBox = ({ end, start }) => ({
  height: Math.max(Math.abs(end.y - start.y), MIN_ELEMENT_SIZE),
  width: Math.max(Math.abs(end.x - start.x), MIN_ELEMENT_SIZE),
  x: Math.min(start.x, end.x),
  y: Math.min(start.y, end.y),
});

const createBoxShapeElement = ({ id, point, style, type }) => ({
  ...normalizeBox({ end: point, start: point }),
  id,
  label: "",
  style: createStyle(style),
  type,
});

export const createRectangleElement = ({ id, point, style }) =>
  createBoxShapeElement({ id, point, style, type: CANVAS_ELEMENT_TYPES.RECTANGLE });

export const createDiamondElement = ({ id, point, style }) =>
  createBoxShapeElement({ id, point, style, type: CANVAS_ELEMENT_TYPES.DIAMOND });

export const createEllipseElement = ({ id, point, style }) =>
  createBoxShapeElement({ id, point, style, type: CANVAS_ELEMENT_TYPES.ELLIPSE });

export const createArrowElement = ({ bindings, id, point, style }) => ({
  bindings,
  id,
  style: createStyle({ ...style, fill: TRANSPARENT_CANVAS_FILL }),
  type: CANVAS_ELEMENT_TYPES.ARROW,
  x1: point.x,
  x2: point.x,
  y1: point.y,
  y2: point.y,
});

export const createTextElement = ({ id, point, style }) => ({
  id,
  style: createStyle({ ...style, fill: DEFAULT_TEXT_FILL }),
  text: DEFAULT_TEXT,
  type: CANVAS_ELEMENT_TYPES.TEXT,
  x: point.x,
  y: point.y,
});

export const createPathElement = ({ id, point, style }) => ({
  id,
  points: [point],
  style: createStyle({ ...style, fill: TRANSPARENT_CANVAS_FILL }),
  type: CANVAS_ELEMENT_TYPES.PATH,
});

const TOOL_ELEMENT_FACTORIES = {
  [CANVAS_TOOLS.ARROW]: createArrowElement,
  [CANVAS_TOOLS.DIAMOND]: createDiamondElement,
  [CANVAS_TOOLS.DRAW]: createPathElement,
  [CANVAS_TOOLS.ELLIPSE]: createEllipseElement,
  [CANVAS_TOOLS.RECTANGLE]: createRectangleElement,
  [CANVAS_TOOLS.TEXT]: createTextElement,
};

/**
 * @param {{ bindings?: object, id: string, point: { x: number, y: number }, style?: object, tool: string }} params
 * @returns {object | null}
 */
export const createElementForTool = ({ bindings, id, point, style, tool }) => {
  const createElement = TOOL_ELEMENT_FACTORIES[tool];
  if (!createElement) return null;

  return createElement({ bindings, id, point, style });
};

const DEFAULT_IMAGE_HEIGHT = 180;
const DEFAULT_IMAGE_WIDTH = 240;
const MAX_IMAGE_INSERT_HEIGHT = 360;
const MAX_IMAGE_INSERT_WIDTH = 480;

/**
 * Element size for a freshly inserted image: natural dimensions fitted into
 * the max insert box (never upscaled) so the aspect ratio is preserved, then
 * divided by zoom so the on-screen size is consistent at any zoom level.
 * Falls back to the legacy default box when natural dimensions are unknown.
 * @param {{ naturalSize?: { height: number, width: number } | null, zoom?: number }} params
 * @returns {{ height: number, width: number }}
 */
export const fitImageElementSize = ({ naturalSize, zoom = 1 } = {}) => {
  if (!naturalSize?.width || !naturalSize?.height) {
    return { height: DEFAULT_IMAGE_HEIGHT, width: DEFAULT_IMAGE_WIDTH };
  }

  const scale = Math.min(
    1,
    MAX_IMAGE_INSERT_WIDTH / naturalSize.width,
    MAX_IMAGE_INSERT_HEIGHT / naturalSize.height
  );
  const zoomFactor = zoom > 0 ? zoom : 1;

  return {
    height: (naturalSize.height * scale) / zoomFactor,
    width: (naturalSize.width * scale) / zoomFactor,
  };
};

export const createImageElement = ({ id, point, size, src }) => ({
  height: size?.height ?? DEFAULT_IMAGE_HEIGHT,
  id,
  src,
  type: CANVAS_ELEMENT_TYPES.IMAGE,
  width: size?.width ?? DEFAULT_IMAGE_WIDTH,
  x: point.x,
  y: point.y,
});

export const moveCanvasElement = ({ delta, element }) => {
  if (element.type === CANVAS_ELEMENT_TYPES.ARROW) {
    return moveArrowElement({ delta, element });
  }
  if (element.type === CANVAS_ELEMENT_TYPES.PATH) {
    return movePathElement({ delta, element });
  }

  return { ...element, x: element.x + delta.x, y: element.y + delta.y };
};

export const resizeBoxElement = ({ element, handle, point }) => {
  const frame = getElementFrame(element);
  const box = getResizedBox({ frame, handle, point });

  return { ...element, ...normalizeBox(box) };
};

export const resizeArrowEndpoint = ({ element, endpoint, point, bindings }) => {
  const nextElement = endpoint === "start"
    ? { ...element, bindings, x1: point.x, y1: point.y }
    : { ...element, bindings, x2: point.x, y2: point.y };

  return nextElement;
};

export const updateElementText = ({ element, text }) => ({
  ...element,
  text: text || DEFAULT_TEXT,
});

export const updateRectangleLabel = ({ element, label }) => ({
  ...element,
  label,
});

export const updateElementStyle = ({ element, style }) => ({
  ...element,
  style: {
    ...element.style,
    ...style,
  },
});

export const duplicateCanvasElement = (element) => ({
  ...element,
  id: createCanvasElementId(),
  x: element.x === undefined ? element.x : element.x + DUPLICATE_OFFSET,
  x1: element.x1 === undefined ? element.x1 : element.x1 + DUPLICATE_OFFSET,
  x2: element.x2 === undefined ? element.x2 : element.x2 + DUPLICATE_OFFSET,
  y: element.y === undefined ? element.y : element.y + DUPLICATE_OFFSET,
  y1: element.y1 === undefined ? element.y1 : element.y1 + DUPLICATE_OFFSET,
  y2: element.y2 === undefined ? element.y2 : element.y2 + DUPLICATE_OFFSET,
  bindings: element.bindings ? { ...element.bindings } : element.bindings,
  points: Array.isArray(element.points)
    ? element.points.map((point) => ({
        x: point.x + DUPLICATE_OFFSET,
        y: point.y + DUPLICATE_OFFSET,
      }))
    : element.points,
});

// Bindings survive the move: bound endpoints re-pin to their shapes at
// resolve time, so a moved arrow pivots around its attachments instead of
// detaching. Detaching is the endpoint-drag gesture, not a body move.
const moveArrowElement = ({ delta, element }) => ({
  ...element,
  x1: element.x1 + delta.x,
  x2: element.x2 + delta.x,
  y1: element.y1 + delta.y,
  y2: element.y2 + delta.y,
});

const movePathElement = ({ delta, element }) => ({
  ...element,
  points: element.points.map((point) => ({
    x: point.x + delta.x,
    y: point.y + delta.y,
  })),
});

const getElementFrame = (element) => ({
  height: element.height ?? element.h ?? DEFAULT_RECTANGLE_HEIGHT,
  width: element.width ?? element.w ?? DEFAULT_RECTANGLE_WIDTH,
  x: element.x ?? 0,
  y: element.y ?? 0,
});

const getResizedBox = ({ frame, handle, point }) => {
  const box = {
    end: { x: frame.x + frame.width, y: frame.y + frame.height },
    start: { x: frame.x, y: frame.y },
  };

  if (handle.includes("west")) box.start.x = point.x;
  if (handle.includes("east")) box.end.x = point.x;
  if (handle.includes("north")) box.start.y = point.y;
  if (handle.includes("south")) box.end.y = point.y;

  return box;
};

